import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, ArrowLeft } from "lucide-react";
import Logo, { LOGO_URL } from "@/components/Logo";

export default function Login() {
  const { login, loginEmail } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState("email"); // "email" | "password"
  const [loading, setLoading] = useState(false);

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const u = await loginEmail(email.trim());
      toast.success(`Välkommen, ${u.first_name}!`);
      nav(u.role === "member" ? "/hem" : "/admin", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      if (status === 404) {
        toast.error("Inget konto hittades. Skapa ett först.");
      } else if (status === 403) {
        toast.info("Detta konto använder lösenord — växla nedan.");
        setMode("password");
      } else {
        toast.error(formatApiErrorDetail(detail) || "Inloggning misslyckades");
      }
    } finally { setLoading(false); }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email.trim(), password);
      toast.success("Välkommen tillbaka!");
      nav(u.role === "member" ? "/hem" : "/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Inloggning misslyckades");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      <div className="hidden lg:flex flex-1 relative overflow-hidden" style={{ background: "linear-gradient(135deg, #E1251B 0%, #C21811 60%, #A31108 100%)" }}>
        {/* subtle rose watermark using the party mark, oversized */}
        <img src={LOGO_URL} alt="" aria-hidden="true"
          className="absolute -right-24 -bottom-32 opacity-15 pointer-events-none select-none"
          style={{ height: "820px", filter: "brightness(0) invert(1)" }}/>
        <div className="relative z-10 p-16 flex flex-col justify-between text-white w-full">
          <div>
            <img src={LOGO_URL} alt="Socialdemokraterna Skåne" style={{ height: 96, filter: "brightness(0) invert(1)" }}/>
            <div className="mt-3 text-xs uppercase tracking-[0.3em] font-semibold opacity-80">Kampanjverktyg · Skåne</div>
          </div>
          <div className="max-w-lg">
            <h2 className="font-heading text-6xl font-extrabold leading-[0.95] tracking-tight">Ett samtal i taget bygger framtiden.</h2>
            <p className="mt-6 text-white/85 text-lg leading-relaxed">Snacket är verktyget som håller ihop kampanjen. Ett tryck. Ett samtal. Ett steg närmare valseger.</p>
            <div className="mt-10 pt-8 border-t border-white/20 flex items-center gap-6 text-white/70 text-sm">
              <div><span className="font-heading text-2xl font-extrabold text-white block leading-none">33</span>Arbetarekommuner</div>
              <div><span className="font-heading text-2xl font-extrabold text-white block leading-none">3</span>Nyckelplatser</div>
              <div><span className="font-heading text-2xl font-extrabold text-white block leading-none">1</span>Rörelse</div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-[520px] flex flex-col justify-center px-8 py-12 bg-white">
        <div className="max-w-sm mx-auto w-full">
          <div className="mb-10">
            <Logo variant="inline" size="lg"/>
          </div>

          {mode === "email" ? (
            <>
              <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900 mb-1">Logga in</h1>
              <p className="text-sm text-slate-500 mb-8">Skriv din e-post — inget lösenord behövs.</p>
              <form onSubmit={submitEmail} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email">E-postadress</Label>
                  <Input id="email" data-testid="login-email" type="email" required value={email} onChange={e=>setEmail(e.target.value)} placeholder="namn@exempel.se" className="h-12" autoFocus/>
                </div>
                <Button type="submit" data-testid="login-email-submit" disabled={loading}
                  className="w-full h-12 bg-[#E1251B] hover:bg-[#C81D14] text-white text-base font-semibold rounded-full">
                  {loading ? "Loggar in…" : "Logga in"}
                </Button>
              </form>
              <div className="mt-6 text-center">
                <button data-testid="switch-password" onClick={() => setMode("password")} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
                  <KeyRound size={12}/> Logga in med lösenord (admin)
                </button>
              </div>
              <p className="mt-8 text-sm text-slate-600 text-center">
                Inget konto? <Link to="/registrera-konto" data-testid="login-goto-register" className="font-semibold text-[#E1251B] hover:underline">Skapa konto</Link>
              </p>
            </>
          ) : (
            <>
              <button data-testid="switch-email" onClick={() => setMode("email")} className="text-xs text-slate-500 hover:text-slate-900 mb-4 inline-flex items-center gap-1">
                <ArrowLeft size={12}/> Tillbaka
              </button>
              <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900 mb-1">Admin-inloggning</h1>
              <p className="text-sm text-slate-500 mb-8">För administratörskonton med lösenord.</p>
              <form onSubmit={submitPassword} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="pwemail">E-postadress</Label>
                  <Input id="pwemail" data-testid="login-email" type="email" required value={email} onChange={e=>setEmail(e.target.value)} className="h-12"/>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Lösenord</Label>
                  <Input id="password" data-testid="login-password" type="password" required value={password} onChange={e=>setPassword(e.target.value)} className="h-12"/>
                </div>
                <Button type="submit" data-testid="login-submit" disabled={loading}
                  className="w-full h-12 bg-[#E1251B] hover:bg-[#C81D14] text-white text-base font-semibold rounded-full">
                  {loading ? "Loggar in…" : "Logga in"}
                </Button>
              </form>
              <p className="mt-4 text-center text-sm">
                <Link to="/glomt-losenord" data-testid="login-forgot" className="text-slate-500 hover:text-slate-900 hover:underline">Glömt lösenord?</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
